import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import {
  AiConsentOpsRecord,
  AiConsentState,
  WITHDRAWAL_ROUTE_LABELS,
} from '../../../core/attestations/ai-consent.types';
import { BusinessAiConsentService } from '../../../core/attestations/business-ai-consent.service';
import { AiConsentFactComponent } from './ai-consent-fact';
import {
  ConsentCheckDialogComponent,
  ConsentEmailDialogComponent,
  SENDER_STANDING_OPTIONS,
} from './ai-consent-dialogs';

/**
 * The business's AI-assessment consent as Ops works with it (FLOW-297).
 *
 * 🚨 **Ops records; it never gives, restores, confirms or overrides**
 * (`§A6.7.4.2`). So there are exactly two acts here, and neither has a reject,
 * hold or confirm-first control (`UI-DA-60-B-01`):
 *
 * - **record an emailed withdrawal** — the email and when it ARRIVED are the
 *   only required fields; the arrival is entered from the email and never
 *   defaulted to now. Nothing about the sender is a condition of saving.
 * - **record the director check**, when a withdrawal rested on a director
 *   claim no record showed. Its result changes nothing about the consent.
 *
 * Sits with the documents, never in the risk lens — see `app-ai-consent-fact`.
 */
@Component({
  selector: 'app-ai-consent-ops-record',
  imports: [DatePipe, MatButtonModule, AiConsentFactComponent],
  templateUrl: './ai-consent-ops-record.html',
  styleUrl: './ai-consent-ops-record.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiConsentOpsRecordComponent implements OnInit {
  readonly entityShortId = input.required<string>();
  /** The record payload's `ai_consent`, shown until the Ops record loads. */
  readonly state = input<AiConsentState | null>(null);

  private readonly service = inject(BusinessAiConsentService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly record = signal<AiConsentOpsRecord | null>(null);
  protected readonly current = computed(() => this.record()?.ai_consent ?? this.state());
  protected readonly latest = computed(() => this.record()?.ai_consent ?? null);
  protected readonly canRecordEmail = computed(() => this.current()?.state === 'ACTIVE');
  protected readonly checkPending = computed(() => this.latest()?.director_check_pending === true);
  protected readonly standingOptions = SENDER_STANDING_OPTIONS;
  protected readonly routeLabels = WITHDRAWAL_ROUTE_LABELS;

  ngOnInit(): void {
    this.service
      .getOpsRecord(this.entityShortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (r) => this.record.set(r), error: () => undefined });
  }

  protected standingLabel(value: string): string {
    return this.standingOptions.find((o) => o.value === value)?.label ?? '';
  }

  protected openEmail(): void {
    this.applyOnClose(
      this.dialog.open(ConsentEmailDialogComponent, { data: this.entityShortId() }),
    );
  }

  protected openCheck(): void {
    this.applyOnClose(
      this.dialog.open(ConsentCheckDialogComponent, { data: this.entityShortId() }),
    );
  }

  /** A modal closes with the record only once its write has landed. */
  private applyOnClose(ref: MatDialogRef<unknown, AiConsentOpsRecord>): void {
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((record) => {
        if (record) this.record.set(record);
      });
  }
}
